import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Plus, UserPlus } from 'lucide-react'
import notify from 'components/ui/notify'
import { initializeMyPartners, updateMyPartner } from 'slices/myPartnersSlice'
import { PartnerProjectCardCN, CardGridCN } from 'components/Card'
import { Button as ShadButton } from 'components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from 'components/ui/dialog'
import { Avatar, AvatarImage, AvatarFallback } from 'components/ui/avatar'
import Button from 'components/Button'
import LocationAddPartnerPopup from '../popups/LocationAddPartnerPopup'
import { myPartnersService } from 'services'
import LocationPartnersEmptyState from 'components/LocationPartnersEmptyState'

const LocationProjectPartners = ({ partnerType, location, project }) => {
	const dispatch = useDispatch()

	const [openAddPartnerPopup, setOpenAddPartnerPopup] = useState(false)
	const [popupTitle, setPopupTitle] = useState('')
	const [recordForEdit, setRecordForEdit] = useState(null)
	const [showInvitationsDialog, setShowInvitationsDialog] = useState(false)

	const { _id: locationId, owner } = location
	const ownerId = owner._id

	const userLogin = useSelector(state => state.userLogin)
	const { userInfo } = userLogin
	const userId = userInfo._id

	const userEntities = useSelector(state => state.userList.entities)

	const projectId = project._id
	const assetPartners = project.partners

	// Pending partners for this project (not yet accepted)
	const pendingProjectPartners = assetPartners?.filter(p => !p.partner?.isAccepted) || []

	useEffect(() => {
		dispatch(initializeMyPartners())
	}, [dispatch])

	// The server sends the reminder, naming this project (MYD-547). The browser's
	// EmailJS copy only ever reached info@.
	const handelRemindPartner = async partner => {
		const name = partner.partnerUserId?.name || 'your partner'
		try {
			await myPartnersService().resendMyPartnerInvitation(partner.partner?._id ?? partner.partner, {
				assetType: 'projects',
				assetId: projectId,
			})
			notify({
				title: `Partner Invited!`,
				text: `An email has been sent to ${name} to remind them of your partnership invitation.`,
				icon: 'success',
				button: 'Ok',
			})
		} catch {
			notify({
				title: 'Reminder not sent',
				text: `The reminder to ${name} could not be sent. Please try again.`,
				icon: 'error',
				button: 'Ok',
			})
		}
	}

	const handleUpdatePartner = async partner => {
		await dispatch(updateMyPartner(partner))
		dispatch(initializeMyPartners())
	}

	const addPartnersHandler = () => {
		if (userId === ownerId) {
			setRecordForEdit(project)
			setPopupTitle(`Add Partner`)
			setOpenAddPartnerPopup(true)
		} else {
			notify({
				title: `Add Partner Error!`,
				text: `You must be the project originator of ${location.assetName} project to add a new Partner to the team.`,
				icon: 'error',
				button: 'Ok',
			})
		}
	}

	const hasPartners = assetPartners && assetPartners.length > 0

	return (
		<div>
			{hasPartners && (
				<div className="flex flex-wrap justify-between items-center gap-4 mb-6">
					<div className="flex flex-col gap-1">
						<h2 className="text-2xl font-bold m-0">Partners</h2>
						<span className="text-[0.95rem] text-gray-600">
							Invite teammates and collaborators to work together on this project.
						</span>
					</div>
					<div className="flex gap-3 flex-wrap">
						<Button variant="primary" onClick={addPartnersHandler}>
							<>
								<Plus className="w-4 h-4" />
								<span>Add Partner</span>
							</>
						</Button>
					</div>
				</div>
			)}

			{hasPartners ? (
				<div className="max-w-[860px] mx-auto w-full mt-4 bg-white rounded-lg border border-gray-200 p-5">
					{/* Pending invitations for this project */}
					<div className="relative group rounded-lg border border-gray-200 px-4 py-3 mb-3 flex items-center justify-between">
						<div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-amber-50 text-amber-600 text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">
							Partners who have not yet accepted their invitation to this project.
						</div>
						<span className="text-sm font-medium text-gray-700">
							Pending Invitations
							<span className="ml-2 inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-700">
								{pendingProjectPartners.length}
							</span>
						</span>
						<ShadButton
							variant="primary"
							isOutline
							className="text-xs"
							onClick={() => setShowInvitationsDialog(true)}
						>
							Manage
						</ShadButton>
					</div>

					{/* Partner cards grid */}
					<CardGridCN>
						{assetPartners.map(assetPartner => {
							const partner = userEntities[assetPartner.partnerUserId._id]
							if (!partner) return <h3 key={assetPartner._id}>Partner information unavailable.</h3>
							return (
								<PartnerProjectCardCN
									key={assetPartner._id}
									name={assetPartner.partnerUserId.name}
									title={assetPartner.partnerUserId.title}
									companyName={assetPartner.partnerUserId.company}
									isAccepted={assetPartner.partner.isAccepted}
									isOwner={ownerId === userId}
									partner={assetPartner}
									avatarUrl={assetPartner.partnerUserId.image}
									handelRemindPartner={handelRemindPartner}
									handleUpdatePartner={handleUpdatePartner}
								/>
							)
						})}
					</CardGridCN>
				</div>
			) : (
				<LocationPartnersEmptyState onAddPartner={addPartnersHandler} />
			)}

			<Dialog open={openAddPartnerPopup} onClose={() => setOpenAddPartnerPopup(false)}>
				<DialogContent className="max-w-[800px]">
					<DialogHeader>
						<div className="flex items-center gap-3">
							<div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#0080fe]/10 text-[#0080fe]">
								<UserPlus className="w-5 h-5" />
							</div>
							<div>
								<DialogTitle>{popupTitle}</DialogTitle>
								<DialogDescription>Add a partner to this project</DialogDescription>
							</div>
						</div>
					</DialogHeader>
					<div className="p-5">
						<LocationAddPartnerPopup project={recordForEdit} />
					</div>
				</DialogContent>
			</Dialog>

			{/* Pending Invitations Dialog */}
			<Dialog open={showInvitationsDialog} onClose={() => setShowInvitationsDialog(false)}>
				<DialogContent className="max-w-2xl">
					<DialogHeader>
						<div className="flex items-center gap-3">
							<div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#0080fe]/10 text-[#0080fe]">
								<UserPlus className="w-5 h-5" />
							</div>
							<div>
								<DialogTitle>Pending Invitations</DialogTitle>
								<DialogDescription>View partner invitations</DialogDescription>
							</div>
						</div>
					</DialogHeader>
					<div className="divide-y divide-gray-100">
						{pendingProjectPartners.length === 0 ? (
							<div className="px-5 py-8 text-center text-sm text-gray-400">No pending invitations</div>
						) : (
							pendingProjectPartners.map(assetPartner => (
								<div key={assetPartner._id} className="flex items-center gap-4 px-5 py-4">
									<Avatar className="h-10 w-10 flex-shrink-0">
										<AvatarImage src={assetPartner.partnerUserId?.image} alt={assetPartner.partnerUserId?.name || ''} />
										<AvatarFallback className="text-sm bg-gray-100 text-gray-600">
											{(assetPartner.partnerUserId?.name || '?').charAt(0).toUpperCase()}
										</AvatarFallback>
									</Avatar>
									<div className="flex-1 min-w-0">
										<p className="text-sm font-medium text-gray-900 truncate">{assetPartner.partnerUserId?.name}</p>
										<p className="text-sm text-gray-500 truncate">
											{assetPartner.partnerUserId?.company}
											{assetPartner.partnerUserId?.title && (
												<span className="text-xs text-gray-400">
													{' \u00b7 '}{assetPartner.partnerUserId.title}
												</span>
											)}
										</p>
									</div>
									<ShadButton variant="primary" isOutline className="flex-shrink-0 text-xs" onClick={() => handelRemindPartner(assetPartner)}>
										Remind
									</ShadButton>
								</div>
							))
						)}
					</div>
				</DialogContent>
			</Dialog>
		</div>
	)
}

export default LocationProjectPartners
